import { Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Observable, map, of } from 'rxjs';
import { PermissionService } from '../../core/auth/permission.service';
import { ConfirmDialogComponent, ConfirmDialogData } from '../confirm-dialog/confirm-dialog.component';

/** Server module that gates editing the customer billing-status flag (EB-3820). */
export const BILLING_STATUS_MODULE = 'contract.billingStatus.edit';

/** Copy shown when crossing into the excluded state (billingEnabled -> false). */
const DISABLE_CONFIRM: ConfirmDialogData = {
  title: 'Exclude customer from billing?',
  message:
    'Setting Billing Enabled to No permanently excludes this customer from billing and from some BI queries. The customer will not be invoiced going forward. Are you sure?',
  confirmLabel: 'Exclude from billing',
  cancelLabel: 'Cancel',
  confirmColor: 'warn',
  icon: 'warning',
};

/** Copy shown when crossing out of the excluded state (billingEnabled false -> not-false). */
const ENABLE_CONFIRM: ConfirmDialogData = {
  title: 'Re-enable billing for this customer?',
  message:
    'At the next billing run, ALL open invoices for the entire exclusion period will be generated. To bill only up to today instead, first create a manual invoice (period end = today) under Billing. Continue?',
  confirmLabel: 'Enable billing',
  cancelLabel: 'Cancel',
  confirmColor: 'warn',
  icon: 'warning',
};

@Injectable({ providedIn: 'root' })
export class BillingStatusGuardService {
  private readonly permissionService = inject(PermissionService);
  private readonly dialog = inject(MatDialog);

  /** True if the current user may edit the billing-status flag. */
  canEdit(): boolean {
    return this.permissionService.hasModule(BILLING_STATUS_MODULE);
  }

  /**
   * Confirm a billingEnabled change. Only prompts when crossing the "excluded"
   * boundary (false <-> not-false); immaterial changes (e.g. true<->null) resolve
   * immediately to true. Returns whether the caller may proceed.
   */
  confirmChange(previous: boolean | null, next: boolean | null): Observable<boolean> {
    // AD-5 boundary: excluded state is `false`.
    if ((previous === false) === (next === false)) return of(true);
    const data: ConfirmDialogData = next === false ? DISABLE_CONFIRM : ENABLE_CONFIRM;
    return this.dialog
      .open(ConfirmDialogComponent, { data, width: '480px', panelClass: 'cmt-hlm-dialog' })
      .afterClosed()
      .pipe(map((confirmed) => confirmed === true));
  }
}
